import { type FormEvent, useState } from "react";
import { formatDate } from "@/lib/date";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/api/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useRepositoryDetail } from "@/hooks/useRepositoryDetail";
import { useMembers } from "@/hooks/useMembers";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Loader2, Save } from "lucide-react";

export function RepositoryDetailPage() {
  const { t } = useTranslation();
  const { orgSlug, projectSlug, repoSlug } = useParams<{ orgSlug: string; projectSlug: string; repoSlug: string }>();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const { repository, config, memberships, loading, error, refetch } = useRepositoryDetail(repoSlug ?? null);
  const { members } = useMembers();

  const memberMap = new Map(members.map((m) => [m.id, m]));

  if (!repoSlug) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/${orgSlug}/projects/${projectSlug}`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="text-2xl font-bold">{repository?.name ?? t("repositoryDetail.repositoryFallback")}</h2>
          {repository && (
            <p className="text-muted-foreground">
              <Badge variant="outline" className="mr-2">{repository.slug}</Badge>
              <span className="text-xs">{repository.git_remote_url}</span>
            </p>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : (
        <div className="space-y-8">
          <ConfigSection
            repoId={repoSlug}
            settings={config?.settings ?? {}}
            isAdmin={isAdmin}
            onSaved={refetch}
          />

          <section>
            <h3 className="text-lg font-semibold mb-3">
              {t("repositoryDetail.membershipOverrides", { count: memberships.length })}
            </h3>
            {memberships.length === 0 ? (
              <p className="text-muted-foreground">{t("repositoryDetail.noOverrides")}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("repositoryDetail.table.member")}</TableHead>
                    <TableHead>{t("repositoryDetail.table.roleOverride")}</TableHead>
                    <TableHead>{t("repositoryDetail.table.autonomyOverride")}</TableHead>
                    <TableHead>{t("repositoryDetail.table.since")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {memberships.map((m) => {
                    const member = memberMap.get(m.member_id);
                    return (
                      <TableRow key={m.id}>
                        <TableCell>
                          {member ? (
                            <div>
                              <span className="font-medium">{member.name}</span>
                              <span className="text-muted-foreground text-xs ml-2">{member.email}</span>
                            </div>
                          ) : (
                            <span className="font-mono text-xs text-muted-foreground">{m.member_id}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {m.role_override ? (
                            <Badge variant="secondary">{m.role_override}</Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {m.autonomy_override ? (
                            <Badge variant="outline">{m.autonomy_override}</Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(m.created_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function ConfigSection({
  repoId,
  settings,
  isAdmin,
  onSaved,
}: {
  repoId: string;
  settings: Record<string, unknown>;
  isAdmin: boolean;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const formatted = JSON.stringify(settings, null, 2);
  const [editing, setEditing] = useState(false);
  const [configText, setConfigText] = useState(formatted);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaveError("");
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(configText) as Record<string, unknown>;
    } catch {
      setSaveError(t("repositoryDetail.config.invalidJson"));
      return;
    }
    setSaving(true);
    try {
      await api(`/api/v2/repositories/${repoId}/config`, {
        method: "POST",
        body: JSON.stringify({ settings: parsed }),
      });
      toast.success(t("repositoryDetail.toast.saved"));
      setEditing(false);
      onSaved();
    } catch {
      toast.error(t("repositoryDetail.toast.saveError"));
      setSaveError(t("repositoryDetail.toast.saveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold">{t("repositoryDetail.config.title")}</h3>
        {isAdmin && !editing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setConfigText(formatted);
              setEditing(true);
            }}
          >
            {t("repositoryDetail.config.editButton")}
          </Button>
        )}
      </div>

      {editing ? (
        <form onSubmit={handleSave} className="space-y-3">
          <textarea
            value={configText}
            onChange={(e) => setConfigText(e.target.value)}
            rows={10}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 font-mono text-sm shadow-sm"
            aria-label="Config JSON"
          />
          {saveError && <p className="text-sm text-destructive">{saveError}</p>}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="animate-spin" />
                  {t("repositoryDetail.config.saving")}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {t("repositoryDetail.config.save")}
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              {t("repositoryDetail.config.cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <pre className="rounded-md border bg-muted/50 p-4 text-sm font-mono overflow-auto">
          {formatted}
        </pre>
      )}
    </section>
  );
}
